'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchWithAppAuth } from '@/lib/auth/client-api';

export function mergeCatalogRecords<T>(current: T[], incoming: T[], key: keyof T): T[] {
  return [...new Map([...current, ...incoming].map(item => [item[key], item])).values()];
}
// Only explicit Load more follows a cursor. Refresh supersedes in-flight pages.
export function useCatalogPage<T>(url: string, field: string, key: keyof T) {
  const [records, setRecords] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState<number | null>(null);
  const cursor = useRef<string | null>(null);
  const epoch = useRef(0);
  const pending = useRef<AbortController | null>(null);
  const request = useCallback(async (more: boolean) => {
    if (more && (!cursor.current || pending.current)) return;
    const generation = ++epoch.current;
    pending.current?.abort();
    const controller = new AbortController(); pending.current = controller;
    setLoading(!more); setLoadingMore(more); setError(null);
    try {
      const response = await fetchWithAppAuth(`${url}${more ? `&cursor=${encodeURIComponent(cursor.current!)}` : ''}`, { cache: 'no-store', signal: controller.signal });
      const payload = await response.json();
      if (!response.ok || !Array.isArray(payload[field])) throw new Error(payload.error || 'Unable to load catalog.');
      if (epoch.current !== generation) return;
      setRecords(current => mergeCatalogRecords(more ? current : [], payload[field], key));
      cursor.current = payload.nextCursor || null; setHasMore(Boolean(cursor.current));
      if (typeof payload.total === 'number') setTotal(payload.total);
    } catch (cause) {
      if (epoch.current === generation && !controller.signal.aborted) setError(cause instanceof Error ? cause.message : 'Unable to load catalog.');
    } finally {
      if (epoch.current === generation) { pending.current = null; setLoading(false); setLoadingMore(false); }
    }
  }, [url, field, key]);
  const refresh = useCallback(() => request(false), [request]);
  const loadMore = useCallback(() => request(true), [request]);
  useEffect(() => {
    const generationRef = epoch;
    const handle = window.setTimeout(() => void refresh(), 0);
    return () => { window.clearTimeout(handle); ++generationRef.current; pending.current?.abort(); pending.current = null; };
  }, [refresh]);
  return { records, setRecords, loading, loadingMore, error, hasMore, total, refresh, loadMore };
}
